import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import React from "react";
import styled from "styled-components";
import { ActionPointView, AgendaItem, PublicAmorgUser, fieldValidation } from "../../../amorg-common";
import { deleteAgendaItem, updateAgendaItem } from "../../../api/activityClientApi";
import { Button } from "../../../designSystem/button/Button";
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { colors } from "../../../designSystem/colors/color";
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { EditableText } from "../../../designSystem/compositeComponents/EditableText";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ActionPointListPanel } from "../actionpoints/ActionPointList";
import { Card } from '../../../designSystem/Card';
import { DragButton } from '../../../designSystem/dragndrop/DragButton';
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';
import { HtmlEditorWithSave } from '../../../designSystem/inputs/HtmlEditorWithSave';
import { H4 } from '../../../designSystem/typography/Heading';
import { ActionPointCounterCompositeIcon } from '../../../designSystem/compositeComponents/compositeIcons/ActionPointCounterCompositeIcon';

type AgendaItemPanelProps = {
    activityId: string,
    agendaItem: AgendaItem,
    agendaItemIndex: number,
    agendaItemsCount: number,
    users: PublicAmorgUser[],
    isDragEnabled: boolean,
    expanded: boolean,
    onUpdated: () => void,
    onToggleExpand: (id: string, expand: boolean) => void;
};

export function AgendaItemPanel (props: AgendaItemPanelProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [inEditActionPoint, setInEditActionPoint] = React.useState<ActionPointView | null>(null);
    const [lastCreatedId, setLastCreatedId] = React.useState('');

    const {isOver, setNodeRef: setDroppableNodeRef} = useDroppable({
        id: `agenda_${props.agendaItem.id}`,
        disabled: !props.isDragEnabled,
    });

    const {attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform} = useDraggable({
        id: `agenda_${props.agendaItem.id}`,
        disabled: !props.isDragEnabled,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    React.useEffect(() =>
    {
        if (inEditActionPoint)
        {
            const refreshedActionPoint = props.agendaItem.actionPoints.find(ap => ap.id === inEditActionPoint.id);
            if (refreshedActionPoint)
                setInEditActionPoint(refreshedActionPoint);
            else
                setInEditActionPoint(null);

        }
        else
        {
            if (lastCreatedId)
            {
                const ap = props.agendaItem.actionPoints.find(ap => ap.id === lastCreatedId);
                if (ap)
                {
                    setInEditActionPoint(ap);
                    setLastCreatedId('');
                }
            }
        }
    }, [props.agendaItem]);

    async function handleUpdateTitle (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, value, props.agendaItem.description, props.agendaItem.note);
        props.onUpdated();
    }

    async function handleUpdateDescription (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, props.agendaItem.title, value, props.agendaItem.note);
        props.onUpdated();
    }

    async function handleUpdateNote (value)
    {
        await updateAgendaItem(props.agendaItem.id, props.activityId, props.agendaItem.title, props.agendaItem.description, value);
        props.onUpdated();
    }

    async function handleDeleteAgendaItem ()
    {
        const result = await deleteAgendaItem(props.agendaItem.id, props.activityId);
        if(result.isSuccess)
        {
            props.onUpdated();
        }
    }

    const aps = props.agendaItem.actionPoints;

    return (
        <div
            ref={(node) => {
                setDroppableNodeRef(node);
                setDraggableNodeRef(node);
            }}
            {...attributes}
            style={{
                ...draggableStyle,
            }}>
        <Card variant='lightGray'>
        <Flex flexDirection="column">
                
                <Flex flexDirection="column" gap={8}>
                    
                    <CardSection>
                        <AgendaItemHeaderRow
                            $isOver={isOver}>
                            
                            {props.isDragEnabled &&
                                <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                            }

                            <div>
                                <TextAvatar text={`#${props.agendaItemIndex+1}`} />
                            </div>

                            <ActionPointCounterCompositeIcon
                                actionpoints={aps}
                            />
                            
                            <DeleteConfirmationButton
                                buttonText={'Slet agendapunkt'}
                                confirmationHeading="Slet agendapunkt?"
                                confirmationText="Er du sikker på du ønsker at slette agendapunktet? Tilknyttede handlepunkter vil ligeledes blive slettet."
                                requireKey={false}
                                asIcon
                                variant='destructive-text'
                                onConfirmDelete={handleDeleteAgendaItem} />

                            <Button
                                variant="text"
                                icon={expanded ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'}
                                onClick={() => { setExpanded(!expanded); props.onToggleExpand(props.agendaItem.id, !expanded); }}
                            />
                        </AgendaItemHeaderRow>
                    </CardSection>
                    
                    <CardSection>
                        <EditableText
                            ariaLabel="Agendapunkt titel"
                            value={props.agendaItem.title}
                            onSave={async (value) =>
                            {
                                if (fieldValidation.agendaItem.title(value))
                                    await handleUpdateTitle(value);
                            }}
                            validator={(currentValue) =>
                            {
                                if (fieldValidation.agendaItem.title(currentValue))
                                    return { valid: true };
                                return { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 100 (${currentValue.length}) tegn.` };
                            }}
                            styleMode='H3'
                            placeholder="Indtast titel"
                        />

                        <div style={{margin:'8px 16px 0px 16px'}}>
                            <HtmlEditorWithSave
                                id={`html-editor-${props.agendaItem.id}`}
                                backgroundColor={colors.background.white}
                                value={props.agendaItem.description}
                                onSave={async (value) => { await handleUpdateDescription(value); }}

                                placeholder="Tilføj beskrivelse..."
                            />
                        </div>
                    </CardSection>

                    <AnimatedCollapsibleContent open={expanded}>

                        <Flex flexDirection="column" gap={8}>

                        <CardSection horizontalPadding>
                            <H4>Referat</H4>
                            <HtmlEditorWithSave
                                id={`html-editor-note-${props.agendaItem.id}`}
                                backgroundColor={colors.background.white}
                                value={props.agendaItem.note}
                                onSave={async (value) => { await handleUpdateNote(value); }}
                                placeholder="Tilføj referat..."
                            />
                        </CardSection>

                        <CardSection horizontalPadding>
                            <Flex flexDirection="column">
                                <H4>Handlepunkter</H4>
                                <Card variant='outlined'>
                                    <ActionPointListPanel
                                        mode={ { type: 'agendaItem', agendaItemId: props.agendaItem.id } }
                                        users={props.users}
                                        actionPoints={props.agendaItem.actionPoints.sort((a, b) => a.created < b.created ? -1 : 1)}
                                        onUpdated={props.onUpdated} />
                                </Card> 
                            </Flex>
                        </CardSection>
                        </Flex>

                    </AnimatedCollapsibleContent>

                </Flex>
            
        </Flex>
        </Card>
        </div>
    );
}

const CardSection = styled.section<{horizontalPadding?: boolean}>`
    /* border: 1px dotted red; */
    padding: 0px;
    ${(props) => props.horizontalPadding && `
        padding-left: 16px;
        padding-right: 16px;
    `}
`;

const AgendaItemHeaderRow = styled.div<{ $isOver:boolean }>`
    display: grid;
    grid-template-columns: min-content min-content 1fr min-content min-content;
    align-items: center;
    border-bottom: 1px solid ${props => props.theme.colors.card.light.default.border};
    background: ${props => props.$isOver ? props.theme.colors.card.light.default.background : 'transparent'};
    gap:8px;
`;
